import { useMemo, useState } from 'react';

import { PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme } from '@mui/material';

import { SuggestedPlaybookCard } from './SuggestedPlaybookCard';
import { useAgentContext } from './SuggestedPlaylistsSection.agentContext';
import { SectionHeader } from '../atoms/SectionHeader';
import { handlePlaylistSearch } from '../utils/playlist.utils';

const PLAYLIST_SECTION_STRINGS = appStrings.modules.priorities.suggestedPlaylistsSection;

type SuggestedPlaylistsSectionProps = Readonly<{
  playlists: PriorityPlaylist[];
}>;

export const SuggestedPlaylistsSection = ({ playlists }: SuggestedPlaylistsSectionProps) => {
  useAgentContext(playlists);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredPlaylists = useMemo(
    () => handlePlaylistSearch(playlists, searchTerm),
    [playlists, searchTerm],
  );

  if (playlists.length === 0) {
    return null;
  }

  return (
    <Box sx={containerSx}>
      <SectionHeader
        title={PLAYLIST_SECTION_STRINGS.title}
        description={PLAYLIST_SECTION_STRINGS.description}
        searchPlaceholder={PLAYLIST_SECTION_STRINGS.searchPlaceholder}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isFilteredResultEmpty={filteredPlaylists.length === 0 && playlists.length > 0}
        disabled={playlists.length === 0}
        inline
      />

      <Box sx={cardRowSx}>
        {filteredPlaylists.map((playlist) => (
          <SuggestedPlaybookCard key={playlist.id} playlist={playlist} />
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  maxWidth: '1240px',
  margin: '0 auto',
  width: '100%',
  boxSizing: 'border-box',
  px: backrs.spacing['spacing-none'],
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-3xl'],
  },
});

// Padding with a matching negative margin leaves room for the card hover lift inside the scroll clip.
const cardRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: backrs.spacing['spacing-3xl'],
  padding: backrs.spacing['spacing-md'],
  margin: `calc(-1 * ${backrs.spacing['spacing-md']})`,
});
